import { createContext, useContext, useState, useMemo, useEffect } from "react";
import axios from "axios";

const MonthContext = createContext(null);

const STORAGE_KEY = "swami_period";

function getCurrentPeriod() {
  const d = new Date();
  return { month: d.getMonth() + 1, year: d.getFullYear() };
}

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.month && parsed?.year) return parsed;
    }
  } catch {/* fall through */}
  return getCurrentPeriod();
}

export function MonthProvider({ children }) {
  const [period, setPeriodState] = useState(loadInitial);

  const current = getCurrentPeriod();
  const isCurrent = period.month === current.month && period.year === current.year;
  const isPast = (period.year < current.year) ||
                 (period.year === current.year && period.month < current.month);
  const isFuture = (period.year > current.year) ||
                   (period.year === current.year && period.month > current.month);

  // Persist + attach month/year on every API request automatically
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(period));
  }, [period]);

  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      const url = config.url || "";
      const isAuthPath = url.includes("/api/auth/") || url.includes("/api/swami-sso/") || url.includes("/api/setup");
      if (url.includes("/api/") && !isAuthPath) {
        if (!config.skipPeriod) {
          config.params = { ...(config.params || {}), month: period.month, year: period.year };
        }
      }
      return config;
    });
    return () => axios.interceptors.request.eject(reqId);
  }, [period]);

  const setPeriod = (next) => setPeriodState(next);
  const goToCurrent = () => setPeriod(getCurrentPeriod());

  // Stubbed unlock/lock — period gating was removed. Kept for back-compat
  // so callers that still import them don't break.
  const unlocked = true;
  const unlock = () => true;
  const lock = () => {};

  const value = useMemo(() => ({
    period, setPeriod, isCurrent, isPast, isFuture,
    unlocked, unlock, lock, goToCurrent, currentPeriod: current,
  }), [period, isCurrent, isPast, isFuture]);  // eslint-disable-line

  return <MonthContext.Provider value={value}>{children}</MonthContext.Provider>;
}

export function useMonth() {
  const ctx = useContext(MonthContext);
  if (!ctx) throw new Error("useMonth must be used inside MonthProvider");
  return ctx;
}

export const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
